import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../atoms/Icon';
import SessionManager from '../../utils/sessionManager';

const SuperAdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});

  const session = SessionManager.getCurrentSession();
  const adminUsername = session?.username || 'Admin';

  const handleLogout = () => {
    SessionManager.clearSession('super_admin');
    navigate('/');
  };

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const isActive = (path) => location.pathname === path;
  const isMenuActive = (paths) => paths.some(path => location.pathname.startsWith(path));

  const menuItems = [
    {
      id: 'overview',
      label: 'Overview',
      icon: 'dashboard',
      path: '/super-admin/dashboard',
      single: true
    },
    {
      id: 'management',
      label: 'Management',
      icon: 'business',
      submenu: [
        { label: 'HR Companies', path: '/super-admin/hr-management', icon: 'corporate_fare' },
        { label: 'Course Providers', path: '/super-admin/course-providers', icon: 'school' },
        { label: 'SaaS Providers', path: '/super-admin/saas-providers', icon: 'cloud' }
      ]
    },
    {
      id: 'api',
      label: 'API Keys',
      icon: 'vpn_key',
      path: '/super-admin/api-keys',
      single: true
    },
    {
      id: 'transactions',
      label: 'Transactions',
      icon: 'receipt_long',
      path: '/super-admin/transactions',
      single: true
    },
    {
      id: 'tokens',
      label: 'Token Usage',
      icon: 'analytics',
      path: '/super-admin/token-usage',
      single: true
    },
    {
      id: 'credits',
      label: 'Credits',
      icon: 'account_balance_wallet',
      submenu: [
        { label: 'Credit Requests', path: '/super-admin/credit-requests', icon: 'request_quote' },
        { label: 'Credit Settings', path: '/super-admin/credit-settings', icon: 'settings' },
        { label: 'User Credits', path: '/super-admin/user-credits', icon: 'people' }
      ]
    },
    {
      id: 'payments',
      label: 'Payments',
      icon: 'payment',
      submenu: [
        { label: 'Dashboard', path: '/super-admin/payments', icon: 'dashboard' },
        { label: 'Orders', path: '/super-admin/payments/orders', icon: 'shopping_cart' },
        { label: 'Webhooks', path: '/super-admin/payments/webhooks', icon: 'webhook' }
      ]
    },
    {
      id: 'packages',
      label: 'Packages',
      icon: 'card_giftcard',
      path: '/super-admin/packages',
      single: true
    },
    {
      id: 'verification',
      label: 'Verification',
      icon: 'verified',
      path: '/super-admin/verification',
      single: true
    },
    {
      id: 'demo',
      label: 'Demo Leads',
      icon: 'contact_mail',
      path: '/super-admin/demo-leads',
      single: true
    },
    {
      id: 'phone',
      label: 'Phone Test',
      icon: 'phone',
      path: '/super-admin/phone-test',
      single: true
    }
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarCollapsed ? 'w-20' : 'w-64'
        } bg-white border-r border-gray-200 transition-all duration-300 flex flex-col`}
      >
        {/* Logo & Toggle */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
          {!sidebarCollapsed && (
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <Icon name="admin_panel_settings" className="text-white text-lg" />
              </div>
              <span className="font-bold text-gray-900">Admin Panel</span>
            </div>
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Icon name={sidebarCollapsed ? 'chevron_right' : 'chevron_left'} className="text-gray-600" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          {menuItems.map((item) => (
            <div key={item.id}>
              {item.single ? (
                <button
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center px-4 py-3 text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? 'bg-blue-50 text-blue-600 border-r-4 border-blue-600'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon name={item.icon} className="text-xl" />
                  {!sidebarCollapsed && <span className="ml-3">{item.label}</span>}
                </button>
              ) : (
                <div>
                  <button
                    onClick={() => toggleMenu(item.id)}
                    className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium transition-colors ${
                      isMenuActive(item.submenu.map(sub => sub.path))
                        ? 'bg-gray-50 text-gray-900'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center">
                      <Icon name={item.icon} className="text-xl" />
                      {!sidebarCollapsed && <span className="ml-3">{item.label}</span>}
                    </div>
                    {!sidebarCollapsed && (
                      <Icon
                        name={expandedMenus[item.id] ? 'expand_less' : 'expand_more'}
                        className="text-gray-400"
                      />
                    )}
                  </button>
                  {!sidebarCollapsed && expandedMenus[item.id] && (
                    <div className="bg-gray-50">
                      {item.submenu.map((subItem) => (
                        <button
                          key={subItem.path}
                          onClick={() => navigate(subItem.path)}
                          className={`w-full flex items-center px-4 py-2.5 pl-12 text-sm transition-colors ${
                            isActive(subItem.path)
                              ? 'text-blue-600 bg-blue-50 border-r-4 border-blue-600'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          }`}
                        >
                          <Icon name={subItem.icon} className="text-lg mr-2" />
                          {subItem.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* User Profile */}
        <div className="border-t border-gray-200 p-4">
          {!sidebarCollapsed ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">
                    {adminUsername.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{adminUsername}</p>
                  <p className="text-xs text-gray-500">Super Admin</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Logout"
              >
                <Icon name="logout" className="text-gray-600" />
              </button>
            </div>
          ) : (
            <button
              onClick={handleLogout}
              className="w-full p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Logout"
            >
              <Icon name="logout" className="text-gray-600" />
            </button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {children}
      </main>
    </div>
  );
};

export default SuperAdminLayout;
